import { useState, useCallback } from "react";
import { toPng, toJpeg, toBlob } from "html-to-image";

export function useExportImage() {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const exportToPng = useCallback(
    async (
      element: HTMLElement | null,
      filename = "export.png",
      options: {
        scale?: number;
        backgroundColor?: string;
        [key: string]: any;
      } = {}
    ) => {
      if (!element) return;

      setIsExporting(true);
      setError(null);
      try {
        const dataUrl = await toPng(element, {
          cacheBust: true,
          pixelRatio: options.scale || 2, // 2x for retina
          backgroundColor: options.backgroundColor || "#ffffff",
          ...options,
        });
        // Trigger download
        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        link.click();

        return dataUrl;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        console.error("Export failed:", err);
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  const exportToJpeg = useCallback(
    async (
      element: HTMLElement | null,
      filename = "export.jpg",
      options: {
        scale?: number;
        backgroundColor?: string;
        quality?: number;
        [key: string]: any;
      } = {}
    ) => {
      if (!element) return;

      setIsExporting(true);
      setError(null);
      try {
        const dataUrl = await toJpeg(element, {
          cacheBust: true,
          pixelRatio: options.scale || 2,
          backgroundColor: options.backgroundColor || "#ffffff",
          quality: options.quality || 0.92,
          ...options,
        });
        // Trigger download
        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        link.click();

        return dataUrl;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        console.error("Export failed:", err);
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  const exportToBlob = useCallback(
    async (
      element: HTMLElement | null,
      options: {
        scale?: number;
        backgroundColor?: string;
        [key: string]: any;
      } = {}
    ) => {
      if (!element) return null;

      setIsExporting(true);
      setError(null);
      try {
        const blob = await toBlob(element, {
          cacheBust: true,
          pixelRatio: options.scale || 2,
          backgroundColor: options.backgroundColor || "#ffffff",
          ...options,
        });
        return blob;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        console.error("Export failed:", err);
        return null;
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  return { exportToPng, exportToJpeg, exportToBlob, isExporting, error };
}
